import { useState, MouseEvent, useEffect, useCallback } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ITestPurchaseMethod } from 'APIPackage/CatalogsModule/interfaces/ITestPurchaseMethod';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { getCatalogTestPurchaseMethods } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';

import { adminCreateCatalogTypes, adminGetCatalogTypes, adminUpdateCatalogTypes } from '../actions/adminAction';
import styles from './AdminEditForm.module.scss';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';

type TestPurchaseMethodType = Omit<ITestPurchaseMethod, 'id'>;

interface ITestPurchaseMethodTypeEditFormProps {
  onBack: () => void;
}

interface ITestPurchaseMethodTypeEditFormStoreProps {
  testPurchaseMethods: ITestPurchaseMethod[];
}

const TestPurchaseMethodTypeEditFormWithoutStore = ({
  onBack,
  testPurchaseMethods,
}: ITestPurchaseMethodTypeEditFormProps & ITestPurchaseMethodTypeEditFormStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [selectedTestPurchaseMethodType, setSelectedTestPurchaseMethodType] = useState<
    ITestPurchaseMethod | undefined
  >();
  const [isNew, setIsNew] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const onRowClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const { id } = event.currentTarget.dataset;
      const testPurchaseMethod = testPurchaseMethods.find(k => k.id.toString() === id);

      setSelectedTestPurchaseMethodType(prev => (prev?.id === testPurchaseMethod?.id ? undefined : testPurchaseMethod));
    },
    [testPurchaseMethods],
  );

  useEffect(() => {
    dispatch(adminGetCatalogTypes(CommonCatalogsNamesEnum.TEST_PURCHASE_METHODS));
  }, [dispatch]);

  const onCreate = useCallback(() => {
    setIsNew(true);
    setSelectedTestPurchaseMethodType({
      id: -1,
      enabled: true,
      code: '',
      name: '',
    });
    setIsFormOpen(true);
  }, []);

  const onEdit = useCallback(() => setIsFormOpen(true), []);

  const onCancel = useCallback(() => {
    setIsNew(false);
    setSelectedTestPurchaseMethodType(undefined);
    setIsFormOpen(false);
  }, []);

  const onSubmit = useCallback(
    (values: TestPurchaseMethodType) => {
      if (isNew) {
        dispatch(adminCreateCatalogTypes({ data: values, catalogName: CommonCatalogsNamesEnum.TEST_PURCHASE_METHODS }));
      } else {
        dispatch(
          adminUpdateCatalogTypes({
            data: { id: selectedTestPurchaseMethodType!.id, ...values },
            catalogName: CommonCatalogsNamesEnum.TEST_PURCHASE_METHODS,
          }),
        );
      }
      onCancel();
    },
    [dispatch, isNew, onCancel, selectedTestPurchaseMethodType],
  );

  const renderForm = useCallback(
    ({ handleSubmit }: FormRenderProps<TestPurchaseMethodType>) => (
      <form onSubmit={handleSubmit}>
        <Row>
          <FieldBlock label="Название" color="black">
            <TextInputField name="name" validate={makeRequired('Поле обязательно')} />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Код" color="black">
            <TextInputField name="code" disabled={!isNew} />
          </FieldBlock>
        </Row>
        <Row>
          <CheckboxField id="enabled" name="enabled" label="Активен" />
        </Row>
        <ModalActions placement="left" padding="none">
          <Button color="primary" text="Сохранить" id="saveButton" type="submit" variant="contained" />
          <Button color="default" text="Отмена" variant="contained" onClick={onCancel} />
        </ModalActions>
      </form>
    ),
    [isNew, onCancel],
  );

  if (isFormOpen) {
    return (
      <>
        <Form<TestPurchaseMethodType>
          onSubmit={onSubmit}
          initialValues={selectedTestPurchaseMethodType}
          render={renderForm}
        />
      </>
    );
  }

  return (
    <div>
      <div className={styles.Table}>
        <span className={styles.Header}>Способ проведения контрольной закупки</span>
        {testPurchaseMethods &&
          testPurchaseMethods.map((testPurchaseMethod: ITestPurchaseMethod) => (
            <button
              key={testPurchaseMethod.id}
              className={
                selectedTestPurchaseMethodType?.id === testPurchaseMethod.id ? styles.DataRowSelected : styles.DataRow
              }
              data-id={testPurchaseMethod.id}
              onClick={onRowClick}
              type="button"
            >
              {testPurchaseMethod.name}
            </button>
          ))}
      </div>
      <div className={styles.ControlButtons}>
        <Button
          className={styles.Button}
          color="default"
          type="button"
          variant="contained"
          onClick={onBack}
          text="Назад"
        />
        <Button
          className={styles.Button}
          color="primary"
          type="button"
          variant="contained"
          onClick={onCreate}
          text="Создать"
          id="createButton"
        />
        {selectedTestPurchaseMethodType && (
          <Button
            className={styles.Button}
            color="default"
            type="button"
            variant="contained"
            onClick={onEdit}
            text="Редактировать"
          />
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IRootState): ITestPurchaseMethodTypeEditFormStoreProps => ({
  testPurchaseMethods: getCatalogTestPurchaseMethods(state),
});

export const TestPurchaseMethodTypeEditForm = connect(mapStateToProps)(TestPurchaseMethodTypeEditFormWithoutStore);
